<template>
  <div
    v-show="items.length > 0"
    class="mx-auto"
    :style="{ width: $vuetify.breakpoint.smAndDown ? '95%' : '700px' }"
  >
    <h3 class="green-heading-text text-center">その他の容量項目</h3>
    <div class="carousel-container">
      <!-- Left arrow control -->
      <button
        v-if="items.length > 1"
        class="arrow-control left-arrow"
        @click="prevImage"
        :disabled="isAnimating"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <polyline points="15 18 9 12 15 6"></polyline>
        </svg>
      </button>

      <div
        class="carousel"
        ref="itemCarouselRef"
        :class="{ 'single-image': items.length === 1 }"
      >
        <div
          v-for="(item, index) in items"
          :key="index"
          class="carousel-item d-flex justify-center align-center"
          :class="{ active: currentIndex === index }"
          @click="setActiveImage(index)"
        >
          <img
            :src="item.images"
            :alt="item.商品名 || `Image ${index + 1}`"
            :style="{
              height: $vuetify.breakpoint.smAndDown ? '100px' : '300px',
            }"
            contain
          />
          <v-row justify="center" class="image-info">
            <v-col cols="12" class="text-center font-weight-bold my-2">
              <p class="mb-0">
                {{ `¥${formatPrice(item.参考価格)}` }}
              </p>
              <p class="mb-0">
                {{ `${item.商品名} (${item.容量})` }}
              </p>
            </v-col>
          </v-row>
        </div>
      </div>

      <!-- Right arrow control -->
      <button
        v-if="items.length > 1"
        class="arrow-control right-arrow"
        @click="nextImage"
        :disabled="isAnimating"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <polyline points="9 18 15 12 9 6"></polyline>
        </svg>
      </button>

      <!-- Navigation dash signs -->
      <div v-if="items.length > 1" class="carousel-dash-signs">
        <span
          v-for="(_, index) in items"
          :key="`dash-${index}`"
          class="dash-sign"
          :class="{ active: currentIndex === index }"
          @click="setActiveImage(index)"
        >
          —
        </span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "ProductCarousel",
  props: {
    items: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      currentIndex: 0,
      isAnimating: false,
    };
  },
  methods: {
    setActiveImage(index) {
      if (this.isAnimating) return;
      this.isAnimating = true;
      this.currentIndex = index;
      this.scrollToCurrentImage();

      setTimeout(() => {
        this.isAnimating = false;
      }, 500);
    },

    scrollToCurrentImage() {
      if (this.$refs.itemCarouselRef) {
        const items =
          this.$refs.itemCarouselRef.querySelectorAll(".carousel-item");
        const selectedItem = items[this.currentIndex];

        if (selectedItem) {
          const containerWidth = this.$refs.itemCarouselRef.offsetWidth;
          const itemWidth = selectedItem.offsetWidth;
          const scrollPosition =
            selectedItem.offsetLeft - containerWidth / 2 + itemWidth / 2;

          this.$refs.itemCarouselRef.scrollTo({
            left: scrollPosition,
            behavior: "smooth",
          });
        }
      }
    },

    nextImage() {
      if (this.isAnimating) return;
      const newIndex = (this.currentIndex + 1) % this.items.length;
      this.setActiveImage(newIndex);
    },

    prevImage() {
      if (this.isAnimating) return;
      const newIndex =
        (this.currentIndex - 1 + this.items.length) % this.items.length;
      this.setActiveImage(newIndex);
    },

    handleKeyDown(e) {
      if (e.key === "ArrowLeft") {
        this.prevImage();
      } else if (e.key === "ArrowRight") {
        this.nextImage();
      }
    },
    formatPrice(price) {
      // Convert to string and split by decimal point
      const [integerPart, decimalPart] = price.toString().split(".");

      // Format the integer part with commas
      const formattedInteger = integerPart.replace(
        /\B(?=(\d{3})+(?!\d))/g,
        ","
      );

      // Return with decimal part if it exists
      if (decimalPart !== undefined) {
        return `${formattedInteger}.${decimalPart}`;
      } else {
        return formattedInteger;
      }
    },
  },
  mounted() {
    // Set up keyboard navigation
    window.addEventListener("keydown", this.handleKeyDown);

    // Initial scroll to active image
    this.$nextTick(() => {
      this.scrollToCurrentImage();
    });
  },
  beforeDestroy() {
    // Clean up event listener
    window.removeEventListener("keydown", this.handleKeyDown);
  },
};
</script>

<style>
.green-heading-text {
  color: #006934;
}

.text-center {
  text-align: center;
}

.carousel-container {
  width: 100%;
  position: relative;
  box-sizing: border-box;
  padding: 10px;
}

.carousel {
  display: flex;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scrollbar-width: none;
  -ms-overflow-style: none;
  gap: 10px;
  padding: 20px 0;
  margin-bottom: 20px;
  position: relative;
  scroll-behavior: smooth;
}

.carousel::-webkit-scrollbar {
  display: none;
}

.carousel-item {
  flex: 0 0 auto;
  width: 72%;
  height: 300px;
  scroll-snap-align: center;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.5s ease;
  opacity: 0.7;
  transform: scale(0.9);
  /* box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2); */
  cursor: pointer;
  position: relative;
}

.carousel-item.active {
  opacity: 1;
  transform: scale(1);
  /* box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3); */
}

.single-image .carousel-item {
  margin: 0 auto;
  width: 60%;
  opacity: 1;
  transform: scale(1);
}

.carousel-image {
  display: block;
}

.image-info {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 0.375rem;
  font-size: 14px;
  text-align: center;
}

.carousel-dash-signs {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 16px;
}

.dash-sign {
  font-size: 36px;
  color: #ddd;
  cursor: pointer;
  transition: color 0.3s ease;
  letter-spacing: 6px;
  line-height: 0.5;
}

.dash-sign.active {
  color: #309c43;
}

.arrow-control {
  position: absolute;
  top: 45%;
  transform: translateY(-50%);
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.8);
  border: none;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  transition: all 0.3s ease;
  color: #333;
}

.arrow-control:hover:not(:disabled) {
  background-color: rgba(255, 255, 255, 0.95);
  transform: translateY(-50%) scale(1.1);
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.3);
}

.arrow-control:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.left-arrow {
  left: 10px;
}

.right-arrow {
  right: 10px;
}
@media screen and (max-width: 600px) {
  .carousel {
    gap: 7px;
    padding: 0px;
    margin-bottom: 20px;
  }
  .carousel-item {
    width: 80%;
    height: 280px;
  }
  .carousel-dash-signs {
    margin-top: 10px;
  }
  .dash-sign {
    font-size: 30px;
  }
  .arrow-control {
    width: 30px;
    height: 30px;
  }
}
</style>
